import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { parseJwt, isTokenExpired } from '../utils/jwt'
import authapi from '../services/auth'
import { resetAuth } from '../store/slices/authSlice'
import { goldToast } from "gold-ui-kit"

const AuthContext = createContext(null)

export const AuthProvider = ({ children }) => {
  const dispatch = useDispatch()
  const token = useSelector((s) => s.auth.token)

  const [user, setUser] = useState(null)
  const [isLogged, setIsLogged] = useState(false)
  const isLoggingOutRef = useRef(false)

  useEffect(() => {
    if (token) {
      isLoggingOutRef.current = false
      try {
        const parsedUser = parseJwt(token)
        if (!isTokenExpired(parsedUser.exp)) {
          setUser(parsedUser)
          setIsLogged(true)
          return
        }
      } catch (err) {}
    }
    setUser(null)
    setIsLogged(false)
  }, [token])

  const logout = useCallback(async (isAutoLogout = false) => {
    if (isLoggingOutRef.current) return
    isLoggingOutRef.current = true

    if (isAutoLogout) {
      goldToast.error("Güvenliğiniz için oturumunuz sonlandırıldı. Lütfen tekrar giriş yapın.", { title: "Oturum Süreniz Doldu" })
    }

    try {
      await authapi.post('auth/logout', {}, { _skip401Handler: true })
    } catch (err) {
      // Çıkış API isteği hatasını göz ardı et (sonsuz döngüyü engelle)
    } finally {
      dispatch(resetAuth())
      setTimeout(() => {
        isLoggingOutRef.current = false
      }, 1000)
    }
  }, [dispatch])

  const hasRole = useCallback((role) => user?.roles?.includes(role) ?? false, [user])
  const hasPerm = useCallback((perm) => user?.permissions?.includes(perm) ?? false, [user])

  /* Token süresi dolduysa otomatik çıkış */
  useEffect(() => {
    if (!user || !user.exp) return
    const msLeft = user.exp * 1000 - Date.now()
    if (msLeft <= 0) { logout(true); return }
    const timer = setTimeout(() => logout(true), msLeft)
    return () => clearTimeout(timer)
  }, [user, logout])

  /* auth-failed (401) olayında otomatik çıkış */
  useEffect(() => {
    const onFailed = () => logout(true)
    window.addEventListener('auth-failed', onFailed)
    return () => window.removeEventListener('auth-failed', onFailed)
  }, [logout])

  return (
    <AuthContext.Provider value={{ token, user, isLogged, logout, hasRole, hasPerm }}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider')
  return ctx
}

